// src/components/Doctor/DoctorPortal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import {
  SUPPORTED_LANGUAGES,
  isRtlLanguage,
} from "../../config/languages";
import "./DoctorPortal.css";
import drIsInLogo from "../../assets/dr-is-in.png";
import caseyImage from "../../assets/Casey_2.png";
import VideoRoom from "../Realtime/VideoRoom";
import ErhAlertPopup from "../AuraERH/ErhAlertPopup";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");

const STORES: Storage[] = [sessionStorage, localStorage];

function clean(v: any) {
  return String(v ?? "").trim();
}

function getFirst(keys: string[]): string {
  for (const store of STORES) {
    for (const k of keys) {
      const v = store.getItem(k);
      if (v && String(v).trim()) return String(v).trim();
    }
  }
  return "";
}

function getAuthToken(): string {
  return (
    getFirst(["doctor_access_token", "doctor_token", "access_token", "token"]) ||
    localStorage.getItem("doctor_access_token") ||
    localStorage.getItem("doctor_token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

function resolveUrl(url?: string | null) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const slash = url.startsWith("/") ? "" : "/";
  return `${API_BASE_URL}${slash}${url}`;
}

function normalizeWebsite(url?: string | null) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
}

function formatFastApiError(err: any): string {
  const data = err?.response?.data ?? err?.data ?? err;
  const detail = data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => {
        const loc = Array.isArray(d?.loc) ? d.loc.join(".") : "";
        const msg = d?.msg ?? "Validation error";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" | ");
  }

  if (typeof detail === "string") return detail;
  if (typeof data === "string") return data;

  try {
    return JSON.stringify(data);
  } catch {
    return String(data ?? "Unknown error");
  }
}

type Doctor = {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  specialties?: string | null;
  country?: string | null;
  state?: string | null;
  website?: string | null;
  medical_license_number?: string | null;
  medical_field?: string | null;
  status?: string | null;
  bio?: string | null;
  photo_url?: string | null;
  license_url?: string | null;
  cv_url?: string | null;
  is_online?: boolean;
  is_approved?: boolean;
};

type ChatMsg = {
  id: string;
  from: "doctor" | "student" | "system";
  text: string;
  ts?: number;
  pending?: boolean;
  client_msg_id?: string;
  originalText?: string;
  translated?: boolean;
};

type IncomingInvite = {
  invite_id: string;
  room_id: string;
  mode: "chat" | "video";
  aura_sos?: boolean;
  incident_id?: string;
  from_student_pk?: string;
  student_id?: string;
  org_code?: string;
  display_name?: string;
  severity?: string;
  ts?: number;
};

type ErhAssistanceRequest = {
  incident_id: string;
  incident_lead_id?: string;
  incident_lead_name?: string;
  target_advisor_id?: string;
  ts?: number;
};

async function translateChatText(text: string, target: string, source = "auto"): Promise<string> {
  const raw = clean(text);
  const lang = clean(target || "en").toLowerCase();

  if (!raw || !lang) return raw;

  try {
    const res = await api.post("/translate", {
      text: raw,
      target: lang,
      source,
    });

    const translated = clean(res?.data?.translated_text);
    return translated || raw;
  } catch (err) {
    console.warn("Advisor chat translation failed", err);
    return raw;
  }
}

const WS_TYPES = {
  CHAT_JOIN: "chat.join",
  CHAT_JOINED: "chat.joined",
  CHAT_MESSAGE: "chat.message",
  INVITE_INCOMING: "invite.incoming",
  INVITE_ACCEPT: "invite.accept",
  INVITE_DECLINE: "invite.decline",
  PRESENCE_GET: "presence.get",
  PRESENCE_UPDATE: "presence.update",
};

function makeClientId() {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function makePkRoomId(studentPk: string, doctorId: number | string) {
  const spk = clean(studentPk) || "0";
  const did = clean(String(doctorId)) || "0";
  return `dm:PK:${spk}:${did}`;
}

async function setAdvisorPresenceRow(_doctorId: number | string, _isOnline: boolean, _doctor?: Doctor | null) {
  // Temporarily disabled.
  // analytics.fact_presence currently does not match the expected schema.
  // Realtime chat/video does not depend on this function.
  return;
}

function buildDoctorWsUrl(apiBase: string, d: Doctor, doctorId: number | string) {
  const base = (apiBase || "http://127.0.0.1:8000").replace(/\/+$/, "");
  const wsBase = base.startsWith("https://") ? base.replace(/^https:/, "wss:") : base.replace(/^http:/, "ws:");
  const qs = new URLSearchParams();
  qs.set("role", "doctor");
  qs.set("doctor_id", String(doctorId || "0"));

  if (d?.name) qs.set("name", d.name);
  if (d?.email) qs.set("email", d.email);
  if (d?.status) qs.set("status", d.status);
  qs.set("is_available", d?.is_online ? "1" : "0");

  return `${wsBase}/realtime/ws?${qs.toString()}`;
}

type TipCategory = "physical" | "mental" | "everyday";

type TipRow = {
  id: number;
  category: string;
  title?: string | null;
  body: string;
  image_url?: string | null;
  author_name?: string | null;
  created_at?: string | null;
};

function safeArray<T>(v: any): T[] {
  return Array.isArray(v) ? v : [];
}

function normalizeTipRows(raw: any): TipRow[] {
  const arr = safeArray<any>(raw);
  const out: TipRow[] = [];
  for (const t of arr) {
    const idNum = Number(t?.id);
    if (!Number.isFinite(idNum)) continue;
    out.push({
      id: idNum,
      category: String(t?.category ?? ""),
      title: t?.title ?? null,
      body: String(t?.body ?? t?.text ?? ""),
      image_url: t?.image_url ?? null,
      author_name: t?.author_name ?? null,
      created_at: t?.created_at ?? null,
    });
  }
  return out;
}

async function fetchPublicTips(category: TipCategory): Promise<TipRow[]> {
  const url = `${API_BASE_URL}/tips/public?category=${encodeURIComponent(category)}&_ts=${Date.now()}`;
  const res = await fetch(url, { cache: "no-store" });
  const data = await res.json().catch(() => []);
  return normalizeTipRows(data);
}

const DoctorPortal: React.FC = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const doctorIdRef = useRef<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [newPhoto, setNewPhoto] = useState<File | null>(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    specialties: "",
    country: "",
    state: "",
    website: "",
    medical_license_number: "",
    medical_field: "",
    status: "",
    bio: "",
  });

  const [tipsOpen, setTipsOpen] = useState(true);

  const [tipsLoading, setTipsLoading] = useState(false);
  const [tipsError, setTipsError] = useState("");
  const [tipsPhysical, setTipsPhysical] = useState<TipRow[]>([]);
  const [tipsMental, setTipsMental] = useState<TipRow[]>([]);
  const [tipsEveryday, setTipsEveryday] = useState<TipRow[]>([]);

  const refreshTips = async () => {
    setTipsLoading(true);
    setTipsError("");
    try {
      const [p, m, e] = await Promise.all([
        fetchPublicTips("physical"),
        fetchPublicTips("mental"),
        fetchPublicTips("everyday"),
      ]);
      setTipsPhysical(p);
      setTipsMental(m);
      setTipsEveryday(e);
    } catch {
      setTipsError("Failed to load tips (check backend /tips/public).");
    } finally {
      setTipsLoading(false);
    }
  };

  useEffect(() => {
    void refreshTips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [tipCategory, setTipCategory] = useState<TipCategory>("physical");
  const [tipTitle, setTipTitle] = useState("");
  const [tipText, setTipText] = useState("");
  const [tipSubmitting, setTipSubmitting] = useState(false);
  const [tipNotice, setTipNotice] = useState<string>("");

  const openTipModal = () => {
    setTipNotice("");
    setTipCategory("physical");
    setTipTitle("");
    setTipText("");
    setTipModalOpen(true);
  };

  const closeTipModal = () => {
    if (tipSubmitting) return;
    setTipModalOpen(false);
  };

  const submitTip = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!doctor) return;

    const body = clean(tipText);
    const title = clean(tipTitle);

    if (body.length < 10) {
      setTipNotice("Please write a longer contribution before submitting.");
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      setTipNotice("Missing token. Please log in again.");
      return;
    }

    setTipSubmitting(true);
    setTipNotice("");
    setErr("");
    setMsg("");

    try {
      const fd = new FormData();
      fd.append("category", tipCategory);
      fd.append("author_name", doctor?.name || "Doctor");
      if (title) fd.append("title", title);
      fd.append("body", body);

      const tipEndpoints = [
        "/tips/doctor/submit",
        "/doctor/tips/submit",
        "/advisor/tips/submit",
      ];

      let submitted = false;
      let lastError: any = null;
      for (const endpoint of tipEndpoints) {
        try {
          await api.post(endpoint, fd, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          submitted = true;
          break;
        } catch (tipError: any) {
          lastError = tipError;
          const status = tipError?.response?.status;
          if (status !== 404 && status !== 405) throw tipError;
        }
      }

      if (!submitted) throw lastError || new Error("Health tip endpoint not found");

      setTipNotice("Casey Academy contribution submitted ✅ Pending approval.");
      setMsg("Casey Academy contribution submitted ✅ (pending admin approval).");
    } catch (e2: any) {
      setTipNotice(formatFastApiError(e2) || "Failed to submit tip.");
      const status = e2?.response?.status;
      if (status === 401 || status === 403) clearAuthAndGoLogin();
    } finally {
      setTipSubmitting(false);
    }
  };

  const photoSrc = useMemo(() => resolveUrl(doctor?.photo_url), [doctor?.photo_url]);
  const websiteHref = useMemo(() => normalizeWebsite(doctor?.website), [doctor?.website]);

  const syncFormFromDoctor = (d: Doctor) => {
    setForm({
      name: d.name || "",
      phone: d.phone || "",
      specialties: d.specialties || "",
      country: d.country || "",
      state: d.state || "",
      website: d.website || "",
      medical_license_number: d.medical_license_number || "",
      medical_field: d.medical_field || "",
      status: d.status || "",
      bio: d.bio || "",
    });
  };

  const clearAuthAndGoLogin = () => {
    ["token", "access_token", "doctor_token", "doctor_access_token", "admin_token", "role", "doctor_id"].forEach((k) => {
      try {
        sessionStorage.removeItem(k);
      } catch {}
      try {
        localStorage.removeItem(k);
      } catch {}
    });
    navigate("/login");
  };

  const fetchMe = async () => {
    try {
      setErr("");
      setMsg("");
      setLoading(true);
      const authToken = getAuthToken();

      const res = await api.get("/doctor/me", {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      const d: Doctor = res.data?.doctor ?? res.data;
      setDoctor(d);
      doctorIdRef.current = d?.id ?? null;
      syncFormFromDoctor(d);

      if (d?.id && d?.is_online) {
        await setAdvisorPresenceRow(d.id, true, d);
      }
    } catch (e: any) {
      const status = e?.response?.status;
      setErr(formatFastApiError(e) || "Unable to load doctor profile.");
      if (status === 401 || status === 403) clearAuthAndGoLogin();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    doctorIdRef.current = doctor?.id ?? null;
  }, [doctor?.id]);

  const logout = async () => {
    try {
      if (doctor?.id) {
        await setAdvisorPresenceRow(doctor.id, false, doctor);
      }
    } catch {}
    clearAuthAndGoLogin();
  };

  const setPresence = async (nextOnline: boolean) => {
    if (!doctor) return;
    const authToken = getAuthToken();
    if (!authToken) {
      setErr("Missing token. Please log in again.");
      return;
    }

    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` };

    try {
      setErr("");
      setMsg("");
      setDoctor((prev) => (prev ? { ...prev, is_online: nextOnline } : prev));

      const body = { is_online: nextOnline };

      let res: any;
      try {
        res = await api.put("/doctor/me", body, { headers });
      } catch (e: any) {
        if (e?.response?.status === 405) res = await api.patch("/doctor/me", body, { headers });
        else throw e;
      }

      const d = res.data?.doctor ?? res.data;
      const nextDoctor = { ...doctor, ...d, is_online: !!d?.is_online } as Doctor;

      setDoctor((prev) => (prev ? { ...prev, ...d, is_online: !!d?.is_online } : prev));
      await setAdvisorPresenceRow(nextDoctor.id, !!d?.is_online, nextDoctor);

      setMsg(nextOnline ? "You are now ONLINE ✅" : "You are now OFFLINE ✅");
    } catch (e: any) {
      setDoctor((prev) => (prev ? { ...prev, is_online: !nextOnline } : prev));
      setErr(formatFastApiError(e) || "Failed to update presence.");
      const status = e?.response?.status;
      if (status === 401 || status === 403) clearAuthAndGoLogin();
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submitUpdateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doctor) return;

    setPending(true);
    setErr("");
    setMsg("");

    try {
      const fd = new FormData();
      let hasAny = false;

      const addIfChanged = (key: keyof typeof form) => {
        const current = String((doctor as any)[key] ?? "").trim();
        const next = String((form as any)[key] ?? "").trim();
        if (next !== "" && next !== current) {
          fd.append(String(key), next);
          hasAny = true;
        }
      };

      addIfChanged("name");
      addIfChanged("phone");
      addIfChanged("specialties");
      addIfChanged("country");
      addIfChanged("state");
      addIfChanged("website");
      addIfChanged("medical_license_number");
      addIfChanged("medical_field");
      addIfChanged("status");
      addIfChanged("bio");

      if (newPhoto) {
        fd.append("photo", newPhoto);
        hasAny = true;
      }

      if (!hasAny) {
        setMsg("No changes detected.");
        setPending(false);
        return;
      }

      const authToken = getAuthToken();
      await api.post("/doctor/me/update_request", fd, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });

      setMsg("Update request submitted ✅ (pending admin approval).");
      setEditOpen(false);
      setNewPhoto(null);
    } catch (e: any) {
      setErr(formatFastApiError(e) || "Failed to submit update request.");
      const status = e?.response?.status;
      if (status === 401 || status === 403) clearAuthAndGoLogin();
    } finally {
      setPending(false);
    }
  };

  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const doctorRef = useRef<Doctor | null>(null);

  const [wsStatus, setWsStatus] = useState<"disconnected" | "connecting" | "connected">("disconnected");
  const [wsError, setWsError] = useState("");

  const [incomingInvites, setIncomingInvites] = useState<IncomingInvite[]>([]);
  const [activeInvite, setActiveInvite] = useState<IncomingInvite | null>(null);
  const [erhAssistanceRequests, setErhAssistanceRequests] = useState<ErhAssistanceRequest[]>([]);

  const [chatStudentPk, setChatStudentPk] = useState(() => localStorage.getItem("doctor_chat_student_pk") || "");
  const [chatRoomId, setChatRoomId] = useState<string>("");
  const chatRoomIdRef = useRef<string>("");
  const [chatText, setChatText] = useState("");
  const [chatLanguage, setChatLanguage] = useState(() => localStorage.getItem("doctor_chat_language") || "en");
  const chatLanguageRef = useRef<string>(chatLanguage);
  const [isTranslatingChat, setIsTranslatingChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const chatMessagesRef = useRef<ChatMsg[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const erhChatChannelRef = useRef<BroadcastChannel | null>(null);
  const activeErhIncidentRef = useRef<string>("");

  const [activeVideoRoomId, setActiveVideoRoomId] = useState<string>("");

  useEffect(() => {
    chatRoomIdRef.current = chatRoomId;
  }, [chatRoomId]);

  useEffect(() => {
    doctorRef.current = doctor;
  }, [doctor]);

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    const incidentId = activeErhIncidentRef.current;
    if (incidentId) {
      try {
        localStorage.setItem(`erh_chat_history:${incidentId}`, JSON.stringify(chatMessages.slice(-200)));
      } catch {}
    }
  }, [chatMessages]);

  useEffect(() => {
    chatLanguageRef.current = chatLanguage;
    localStorage.setItem("doctor_chat_language", chatLanguage);
  }, [chatLanguage]);

  useEffect(() => {
    const bindErh = (incidentId: string, roomId: string) => {
      const canonicalRoomId = clean(roomId);
      if (!incidentId || !canonicalRoomId) return;

      activeErhIncidentRef.current = incidentId;

      // The ERH incident room is authoritative. Never fall back to a stale
      // direct-message room from another Advisor Portal session.
      chatRoomIdRef.current = canonicalRoomId;
      setChatRoomId(canonicalRoomId);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsSend({ type: WS_TYPES.CHAT_JOIN, room_id: canonicalRoomId });
      }

      erhChatChannelRef.current?.close();
      const channel = new BroadcastChannel(`aura-erh-chat:${incidentId}`);
      erhChatChannelRef.current = channel;
      channel.onmessage = (event) => {
        const data = event.data || {};
        if (data.type === "chat.sync.request") {
          channel.postMessage({
            type: "chat.sync.response",
            room_id: canonicalRoomId,
            connected: wsRef.current?.readyState === WebSocket.OPEN,
            messages: chatMessagesRef.current.slice(-200),
          });
          return;
        }
        if (data.type === "chat.send" && clean(data.text)) {
          const client_msg_id = makeClientId();
          setChatMessages((prev) => [...prev, {
            id: client_msg_id,
            from: "doctor",
            text: clean(data.text),
            pending: true,
            client_msg_id,
          }]);
          wsSend({
            type: "chat.message",
            room_id: canonicalRoomId,
            text: clean(data.text),
            client_msg_id,
          });
        }
      };
      localStorage.setItem(`erh_room:${incidentId}`, canonicalRoomId);
      localStorage.setItem(`erh_advisor_name:${incidentId}`, doctorRef.current?.name || "");
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== "erh_bind_request" || !event.newValue) return;
      try {
        const req = JSON.parse(event.newValue);
        if (String(req.advisor_id || "") !== String(doctorIdRef.current || "")) return;
        bindErh(String(req.incident_id || ""), String(req.room_id || ""));
      } catch {}
    };
    const onVideoStorage = (event: StorageEvent) => {
      if (event.key !== "erh_video_open" || !event.newValue) return;
      try {
        const req = JSON.parse(event.newValue);
        if (String(req.advisor_id || "") !== String(doctorIdRef.current || "")) return;
        const roomId = clean(req.room_id);
        if (roomId) setActiveVideoRoomId(roomId);
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("storage", onVideoStorage);
    try {
      const raw = localStorage.getItem("erh_bind_request");
      if (raw) {
        const req = JSON.parse(raw);
        if (String(req.advisor_id || "") === String(doctorIdRef.current || "")) {
          bindErh(String(req.incident_id || ""), String(req.room_id || ""));
        }
      }
    } catch {}
    try {
      const rawVideo = localStorage.getItem("erh_video_open");
      if (rawVideo) {
        const req = JSON.parse(rawVideo);
        if (String(req.advisor_id || "") === String(doctorIdRef.current || "")) {
          const roomId = clean(req.room_id);
          if (roomId) setActiveVideoRoomId(roomId);
        }
      }
    } catch {}
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("storage", onVideoStorage);
    };
  }, []);

  const stopPing = () => {
    if (pingTimerRef.current) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  };

  const startPing = () => {
    stopPing();
    pingTimerRef.current = window.setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
      } catch {}
    }, 15000);
  };

  const wsSend = (obj: any) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch {
      return false;
    }
  };

  const sendPresenceMeta = (reason: string) => {
    const d = doctorRef.current;
    if (!d) return;

    wsSend({
      type: WS_TYPES.PRESENCE_UPDATE,
      meta: {
        name: d.name || "",
        email: d.email || "",
        status: d.status || "",
        specialties: d.specialties || "",
        medical_field: d.medical_field || "",
        is_available: !!d.is_online,
        reason,
      },
    });
  };

  const refreshPendingErhAssistance = async () => {
    const d = doctorRef.current;
    const advisorId = d?.id ?? doctorIdRef.current;
    if (!advisorId) return;
    try {
      const params = new URLSearchParams({ advisor_id: String(advisorId) });
      const response = await fetch(`${API_BASE_URL}/realtime/erh/assistance/pending?${params}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) return;
      const rows: ErhAssistanceRequest[] = Array.isArray(data?.requests)
        ? data.requests.map((item: any) => ({
            incident_id: String(item?.incident_id || ""),
            incident_lead_id: item?.incident_lead_id ? String(item.incident_lead_id) : undefined,
            incident_lead_name: item?.incident_lead_name ? String(item.incident_lead_name) : undefined,
            target_advisor_id: item?.target_advisor_id ? String(item.target_advisor_id) : undefined,
          })).filter((item: ErhAssistanceRequest) => !!item.incident_id)
        : [];
      setErhAssistanceRequests(rows);
    } catch {
      // WebSocket delivery still provides the immediate alert; polling is recovery.
    }
  };

  const disconnectWs = () => {
    stopPing();
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;

    setWsStatus("disconnected");
    setWsError("");

    setChatRoomId("");
    chatRoomIdRef.current = "";
    setChatMessages([]);
    setActiveInvite(null);
    setIncomingInvites([]);
    setActiveVideoRoomId("");
  };

  const connectWs = () => {
    const d = doctorRef.current;
    if (!d?.id && !doctorIdRef.current) {
      setWsError("Doctor profile not loaded yet.");
      return;
    }
    if (!d) {
      setWsError("Doctor profile not loaded yet.");
      return;
    }

    setWsError("");
    setWsStatus("connecting");

    stopPing();
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    try {
      wsRef.current?.close();
    } catch {}

    const did = d?.id ?? doctorIdRef.current ?? 0;
    const url = buildDoctorWsUrl(API_BASE_URL, d, did);

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus("connected");
      setWsError("");

      wsSend({ type: WS_TYPES.PRESENCE_GET });
      sendPresenceMeta("onopen");
      startPing();
      void setAdvisorPresenceRow(did, true, d);
      void refreshPendingErhAssistance();
    };

    ws.onclose = () => {
      stopPing();
      setWsStatus("disconnected");
      setActiveVideoRoomId("");

      const cur = doctorRef.current;
      if (cur?.is_online) {
        reconnectTimerRef.current = window.setTimeout(() => {
          if (doctorRef.current?.is_online) connectWs();
        }, 1200);
      }
    };

    ws.onerror = () => {
      stopPing();
      setWsError("WebSocket error (check backend running on 8000 + WS URL).");
      setWsStatus("disconnected");
      setActiveVideoRoomId("");
    };

    ws.onmessage = (ev) => {
      try {
        const raw = JSON.parse(ev.data);
        const type = String(raw?.type || raw?.event || raw?.action || "").toLowerCase();
        const payload = raw?.payload ?? raw?.data ?? raw;

        if (type === "presence.snapshot") return;

        if (type === "aura.erh.assistance.requested") {
          const req: ErhAssistanceRequest = {
            incident_id: String(payload?.incident_id || ""),
            incident_lead_id: payload?.incident_lead_id ? String(payload.incident_lead_id) : undefined,
            incident_lead_name: payload?.incident_lead_name ? String(payload.incident_lead_name) : undefined,
            target_advisor_id: payload?.target_advisor_id ? String(payload.target_advisor_id) : undefined,
            ts: typeof payload?.ts === "number" ? payload.ts : undefined,
          };
          if (req.incident_id) {
            setErhAssistanceRequests((prev) => prev.some((x) => x.incident_id === req.incident_id) ? prev : [req, ...prev].slice(0, 20));
          }
          return;
        }

        if (type === WS_TYPES.INVITE_INCOMING) {
          const modeRaw = String(payload?.mode || "chat").toLowerCase();
          const mode: "chat" | "video" = modeRaw.includes("video") ? "video" : "chat";

          const inv: IncomingInvite = {
            invite_id: String(payload?.invite_id || makeClientId()),
            room_id: String(payload?.room_id || ""),
            mode,
            aura_sos: !!payload?.aura_sos,
            incident_id: payload?.incident_id ? String(payload.incident_id) : undefined,
            from_student_pk: String(payload?.from_student_pk || ""),
            student_id: payload?.student_id ? String(payload.student_id) : undefined,
            org_code: payload?.org_code ? String(payload.org_code) : undefined,
            display_name: payload?.display_name ? String(payload.display_name) : undefined,
            severity: payload?.severity ? String(payload.severity) : undefined,
            ts: typeof payload?.ts === "number" ? payload.ts : undefined,
          };

          if (!inv.room_id) {
            const spk = inv.from_student_pk || "";
            const did2 = String(doctorIdRef.current ?? d?.id ?? "");
            inv.room_id = makePkRoomId(spk, did2);
          }

          setIncomingInvites((prev) => {
            const exists = prev.some((x) => x.room_id === inv.room_id);
            if (exists) return prev;
            return [inv, ...prev].slice(0, 25);
          });

          setActiveInvite((cur) => cur || inv);

          if (inv.from_student_pk) {
            setChatStudentPk((prev) => (prev?.trim() ? prev : inv.from_student_pk || ""));
          }

          return;
        }

        if (type === WS_TYPES.CHAT_JOINED) return;

        if (type === WS_TYPES.CHAT_MESSAGE) {
          const p = raw?.payload ?? raw?.data ?? raw;

          const text = String(p?.text ?? p?.message ?? raw?.text ?? raw?.message ?? "");

          const room = String(p?.room_id ?? raw?.room_id ?? "");
          const activeRoom = chatRoomIdRef.current;
          if (activeRoom && room && room !== activeRoom) return;

          const clientMsgId = String(p?.client_msg_id ?? raw?.client_msg_id ?? "");
          const serverMsgId = String(p?.server_msg_id ?? raw?.server_msg_id ?? "");
          const id = clientMsgId || serverMsgId || makeClientId();

          const fromRoleRaw = String(
            p?.from ?? p?.from_role ?? p?.sender ?? raw?.from ?? raw?.from_role ?? raw?.sender ?? ""
          ).toLowerCase();

          let from: ChatMsg["from"] = "student";

          if (fromRoleRaw.includes("system")) {
            from = "system";
          } else if (fromRoleRaw.includes("doctor") || fromRoleRaw.includes("advisor")) {
            from = "doctor";
          } else if (fromRoleRaw.includes("student")) {
            from = "student";
          }

          setChatMessages((prev) => {
            const existingIndex = clientMsgId
              ? prev.findIndex((m) => m.client_msg_id === clientMsgId || m.id === clientMsgId)
              : -1;

            if (existingIndex >= 0) {
              const copy = [...prev];
              copy[existingIndex] = {
                ...copy[existingIndex],
                pending: false,
                text: text || copy[existingIndex].text,
                from: copy[existingIndex].from,
              };
              return copy;
            }

            if (!text) return prev;

            return [
              ...prev,
              {
                id,
                from,
                text,
                originalText: text,
                translated: false,
                ts: typeof p?.ts === "number" ? p.ts : undefined,
              },
            ];
          });

          if (activeErhIncidentRef.current && erhChatChannelRef.current && text) {
            erhChatChannelRef.current.postMessage({ type: "chat.message", message: { id, from, text, ts: typeof p?.ts === "number" ? p.ts : Date.now() } });
          }

          const lang = clean(chatLanguageRef.current).toLowerCase();
          if (text && lang && lang !== "en" && from !== "doctor") {
            void translateChatText(text, lang, "auto").then((translatedText) => {
              if (!translatedText || translatedText === text) return;
              setChatMessages((prev) =>
                prev.map((m) =>
                  m.id === id
                    ? { ...m, text: translatedText, originalText: text, translated: true }
                    : m
                )
              );
            });
          }

          return;
        }
      } catch {
        // ignore
      }
    };
  };

  useEffect(() => {
    if (!doctor) return;

    if (doctor.is_online) {
      if (wsStatus === "connected" || wsStatus === "connecting") return;
      connectWs();
    } else {
      if (wsStatus !== "disconnected") disconnectWs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor?.is_online]);

  useEffect(() => {
    if (!doctor?.is_online) return;
    void refreshPendingErhAssistance();
    const timer = window.setInterval(() => {
      void refreshPendingErhAssistance();
    }, 10000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor?.id, doctor?.is_online]);

  useEffect(() => {
    if (wsStatus !== "connected") return;
    if (!doctor) return;
    sendPresenceMeta("doctor_changed");
    void setAdvisorPresenceRow(doctor.id, !!doctor.is_online, doctor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsStatus, doctor?.name, doctor?.email, doctor?.status, doctor?.specialties, doctor?.medical_field, doctor?.is_online]);

  useEffect(() => {
    return () => {
      stopPing();
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      const d = doctorRef.current;
      if (d?.id) void setAdvisorPresenceRow(d.id, false, d);
      try {
        wsRef.current?.close();
      } catch {}
      try { erhChatChannelRef.current?.close(); } catch {}
    };
  }, []);

  const joinRoom = (rid: string) => {
    if (!rid) return;
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setWsError("Socket not connected. Toggle Online first.");
      return;
    }
    setWsError("");
    setChatRoomId(rid);
    chatRoomIdRef.current = rid;
    if (!wsSend({ type: WS_TYPES.CHAT_JOIN, room_id: rid })) {
      setWsError("Failed to join room.");
    }
  };

  const acceptInvite = async (inv: IncomingInvite) => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setWsError("Socket not connected. Toggle Online first.");
      return;
    }

    setWsError("");
    setMsg("");

    wsSend({
      type: WS_TYPES.INVITE_ACCEPT,
      invite_id: inv.invite_id,
      room_id: inv.room_id,
      from_student_pk: inv.from_student_pk,
      student_id: inv.student_id,
      mode: inv.mode,
      incident_id: inv.incident_id,
      aura_sos: !!inv.aura_sos,
    });

    joinRoom(inv.room_id);

    if (inv.mode === "video") setActiveVideoRoomId(inv.room_id);

    setIncomingInvites((prev) => prev.filter((x) => x.invite_id !== inv.invite_id));
    setActiveInvite(null);

    if (inv.aura_sos && inv.incident_id) {
      try {
        activeErhIncidentRef.current = inv.incident_id;
        erhChatChannelRef.current?.close();
        const channel = new BroadcastChannel(`aura-erh-chat:${inv.incident_id}`);
        erhChatChannelRef.current = channel;
        channel.onmessage = (event) => {
          const data = event.data || {};
          if (data.type === "chat.sync.request") {
            channel.postMessage({
              type: "chat.sync.response",
              room_id: inv.room_id,
              connected: wsRef.current?.readyState === WebSocket.OPEN,
              messages: chatMessagesRef.current.slice(-200),
            });
            return;
          }
          if (data.type === "chat.send" && clean(data.text)) {
            const client_msg_id = makeClientId();
            setChatMessages((prev) => [...prev, { id: client_msg_id, from: "doctor", text: clean(data.text), pending: true, client_msg_id }]);
            wsSend({ type: "chat.message", room_id: inv.room_id, text: clean(data.text), client_msg_id });
          }
        };
        localStorage.setItem(`erh_room:${inv.incident_id}`, inv.room_id);
        localStorage.setItem(`erh_student_pk:${inv.incident_id}`, inv.from_student_pk || "");
        localStorage.setItem(`erh_advisor_name:${inv.incident_id}`, doctorRef.current?.name || "");
        localStorage.setItem(`erh_chat_history:${inv.incident_id}`, JSON.stringify(chatMessagesRef.current.slice(-200)));
      } catch {}

      setMsg("AURA ERH incident accepted ✅ — the student chat remains active in this tab. ERH command tools opened in a new tab.");
      // A separate tab preserves this live WebSocket/chat session. The backend
      // currently permits one advisor socket per advisor ID, so the ERH tab is
      // deliberately command-only until the shared-session transport is added.
      window.open(`/doctor-portal/erh/${encodeURIComponent(inv.incident_id)}`, "_blank", "noopener,noreferrer");
    } else {
      setMsg(inv.mode === "video" ? "Video request accepted 🎥 — starting video…" : "Chat request accepted ✅");
    }
  };

  const acceptErhAssistance = async (request: ErhAssistanceRequest) => {
    const d = doctorRef.current;
    if (!d?.id || !request.incident_id) return;
    setWsError("");
    try {
      const params = new URLSearchParams({
        responder_id: String(d.id),
        responder_name: d.name || `Advisor ${d.id}`,
      });
      const response = await fetch(`${API_BASE_URL}/realtime/erh/incidents/${encodeURIComponent(request.incident_id)}/assistance/accept?${params}`, { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) throw new Error(data?.detail || "Unable to accept consulting role");
      setErhAssistanceRequests((prev) => prev.filter((x) => x.incident_id !== request.incident_id));
      setMsg("Consulting Advisor role accepted ✅ — opening the private ERH consultation workspace.");
      window.open(`/doctor-portal/erh/${encodeURIComponent(request.incident_id)}`, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setWsError(e?.message || "Unable to accept consulting role.");
    }
  };

  const declineInvite = (inv: IncomingInvite) => {
    if (wsRef.current && wsStatus === "connected") {
      wsSend({
        type: WS_TYPES.INVITE_DECLINE,
        invite_id: inv.invite_id,
        room_id: inv.room_id,
        from_student_pk: inv.from_student_pk,
        student_id: inv.student_id,
        mode: inv.mode,
      });
    }
    setIncomingInvites((prev) => prev.filter((x) => x.room_id !== inv.room_id));
    setActiveInvite(null);
  };

  const startManualChat = () => {
    setWsError("");
    if (!doctor?.id) {
      setWsError("Doctor profile not loaded yet.");
      return;
    }
    if (!doctor.is_online) {
      setWsError("Toggle Online first (required to connect to realtime).");
      return;
    }
    const spk = clean(chatStudentPk);
    if (!spk) {
      setWsError("Enter Student PK (example: 12).");
      return;
    }

    localStorage.setItem("doctor_chat_student_pk", spk);
    const rid = makePkRoomId(spk, doctor.id);
    joinRoom(rid);
  };

  const sendChat = async () => {
    const text = chatText.trim();
    if (!text) return;

    // React state can lag by one render immediately after Accept & Join.
    // Use the synchronously updated room ref and the browser socket's actual
    // readyState so the advisor can reply on the very first turn.
    const activeRoomId = clean(chatRoomIdRef.current || chatRoomId);
    const activeSocket = wsRef.current;

    if (!activeRoomId || !activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
      setChatMessages((prev) => [
        ...prev,
        { id: makeClientId(), from: "system", text: "Not connected. Toggle Online and join a room first." },
      ]);
      setChatText("");
      return;
    }

    const client_msg_id = makeClientId();
    const targetLang = clean(chatLanguage).toLowerCase();
    const shouldTranslateOutgoing = Boolean(targetLang && targetLang !== "en");

    setIsTranslatingChat(shouldTranslateOutgoing);

    let outgoingText = text;
    if (shouldTranslateOutgoing) {
      outgoingText = await translateChatText(text, targetLang, "auto");
    }

    setChatMessages((prev) => [
      ...prev,
      {
        id: client_msg_id,
        from: "doctor",
        text: outgoingText,
        originalText: outgoingText !== text ? text : undefined,
        translated: outgoingText !== text,
        pending: true,
        client_msg_id,
      },
    ]);

    if (activeErhIncidentRef.current && erhChatChannelRef.current) {
      erhChatChannelRef.current.postMessage({ type: "chat.message", message: { id: client_msg_id, from: "doctor", text: outgoingText, ts: Date.now() } });
    }

    if (!wsSend({ type: "chat.message", room_id: activeRoomId, text: outgoingText, client_msg_id })) {
      setWsError("Failed to send (socket closed).");
      setChatMessages((prev) =>
        prev.map((m) =>
          m.client_msg_id === client_msg_id
            ? { ...m, pending: false, text: `${m.text}\n\n[Message was not sent]` }
            : m
        )
      );
    }

    setIsTranslatingChat(false);
    setChatText("");
  };

  if (loading) return <div className="doctor-portal-loading">Loading...</div>;
  if (!doctor) return <div className="doctor-portal-loading">No doctor profile found.</div>;

  const VideoRoomAny = VideoRoom as any;

  const submitTipBtnStyle: React.CSSProperties = {
    height: 40,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "linear-gradient(180deg, rgba(59,130,246,0.98), rgba(37,99,235,0.98))",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 10px 22px rgba(59, 130, 246, 0.28), 0 2px 0 rgba(0,0,0,0.10)",
    textShadow: "0 1px 0 rgba(0,0,0,0.18)",
  };

  const tipSectionCard = (sec: {
    id: TipCategory;
    icon: string;
    title: string;
    subtitle: string;
    tips: TipRow[];
  }) => (
    <div
      key={sec.id}
      style={{
        borderRadius: 16,
        border: "1px solid rgba(0,0,0,0.10)",
        background: "rgba(255,255,255,0.65)",
        padding: 14,
        display: "flex",
        flexDirection: "column",
        minHeight: 220,
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ fontSize: 20 }}>{sec.icon}</div>
        <div style={{ fontWeight: 1000, fontSize: 16 }}>{sec.title}</div>
      </div>
      <div style={{ marginTop: 6, fontWeight: 750, opacity: 0.78, lineHeight: 1.35 }}>{sec.subtitle}</div>

      <div
        className="doctor-tips-scroll"
        style={{
          marginTop: 10,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          overflowY: "auto",
          maxHeight: 340,
          paddingRight: 6,
        }}
      >
        {sec.tips.length === 0 ? (
          <div style={{ fontWeight: 800, opacity: 0.7, padding: "10px 0" }}>No tips yet.</div>
        ) : (
          sec.tips.map((t) => (
            <div
              key={`${sec.id}_${t.id}`}
              style={{
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(255,255,255,0.75)",
                padding: "10px 12px",
                fontWeight: 750,
                lineHeight: 1.35,
                whiteSpace: "pre-wrap",
              }}
            >
              {t.title ? <div style={{ fontWeight: 1000, marginBottom: 4 }}>{t.title}</div> : null}
              <div style={{ opacity: 0.92 }}>{t.body}</div>
              {t.author_name ? (
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7, fontWeight: 800 }}>— {t.author_name}</div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const tipSections = [
    {
      id: "physical" as const,
      icon: "💪",
      title: "Physical Wellness",
      subtitle: "Simple habits that improve energy, strength, and long-term health.",
      tips: tipsPhysical,
    },
    {
      id: "mental" as const,
      icon: "🧠",
      title: "Mental Health Care",
      subtitle: "Tools for stress, focus, and emotional resilience.",
      tips: tipsMental,
    },
    {
      id: "everyday" as const,
      icon: "✅",
      title: "Everyday Healthy Habits",
      subtitle: "Small daily decisions that add up fast for young adults.",
      tips: tipsEveryday,
    },
  ];

  return (
    <div className="doctor-portal-page" style={{ padding: "22px 16px" }}>
      <div className="doctor-portal-card" style={{ maxWidth: 1100, padding: "18px 18px 14px" }}>
        <ErhAlertPopup role="doctor" openRoute={(incidentId) => `/doctor-portal/erh/${encodeURIComponent(incidentId)}`} title="AURA ERH™ — Advisor Emergency Response" />
        <div className="doctor-portal-topbar">
          <img className="doctor-portal-logo" src={drIsInLogo} alt="Doctor Is In" />
          <div className="doctor-portal-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate("/directory")}>
              Advisor Connect
            </button>
            <button type="button" className="btn-danger" onClick={logout}>
              Logout
            </button>
          </div>
        </div>

        <h2 className="doctor-portal-title" style={{ marginTop: 10 }}>
          My Advisor Profile
        </h2>
        <div className="doctor-portal-tagline">AI Wellness Advisor with a human touch.</div>

        {err || msg ? <div className={err ? "notice error" : "notice ok"}>{String(err || msg)}</div> : null}

        <div
          className="doctor-casey-hero"
          style={{
            marginTop: 14,
            borderRadius: 18,
            border: "1px solid rgba(0,0,0,0.08)",
            background: "linear-gradient(135deg, rgba(236,250,255,0.96), rgba(255,255,255,0.92))",
            boxShadow: "0 12px 34px rgba(11,47,91,0.10)",
            padding: "18px 22px",
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            alignItems: "center",
            gap: 16,
            overflow: "hidden",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 1000,
                color: "#0b1f3a",
                lineHeight: 1.1,
              }}
            >
              Welcome, {doctor.name || "Advisor"} 👋
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 14,
                fontWeight: 800,
                color: "rgba(15,23,42,0.78)",
                lineHeight: 1.45,
                maxWidth: 620,
              }}
            >
              Casey supports every student conversation while you provide trusted human guidance and care.
            </div>
            <div
              style={{
                marginTop: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                borderRadius: 999,
                background: doctor.is_online ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.16)",
                border: doctor.is_online ? "1px solid rgba(34,197,94,0.28)" : "1px solid rgba(148,163,184,0.28)",
                color: doctor.is_online ? "#166534" : "#334155",
                fontSize: 12,
                fontWeight: 950,
              }}
            >
              <span>{doctor.is_online ? "● Online and ready" : "● Set yourself online to receive requests"}</span>
            </div>
          </div>

          <div
            style={{
              background: "rgba(255,255,255,0.94)",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 18,
              padding: "12px 14px",
              boxShadow: "0 14px 30px rgba(15,23,42,0.10)",
              fontWeight: 900,
              lineHeight: 1.35,
              color: "#111827",
              maxWidth: 230,
            }}
          >
            <div style={{ fontWeight: 1000 }}>Hi, I’m Casey.</div>
            <div style={{ marginTop: 4, opacity: 0.78 }}>
              I’m here to help you support students with care and confidence.
            </div>
          </div>

          <img
            src={caseyImage}
            alt="Casey AI Wellness Advisor"
            style={{
              width: 132,
              maxHeight: 170,
              objectFit: "contain",
              filter: "drop-shadow(0 16px 22px rgba(15,23,42,0.18))",
            }}
          />
        </div>

        <div className="doctor-portal-header" style={{ marginTop: 12 }}>
          <div className="doctor-portal-photo">
            {photoSrc ? <img src={photoSrc} alt={doctor?.name || "Doctor"} /> : <div className="photo-placeholder" />}
          </div>

          <div className="doctor-portal-summary">
            <div className="name-row">
              <div className="doctor-name">{doctor.name}</div>
              <span className={doctor.is_online ? "pill online" : "pill offline"}>
                {doctor.is_online ? "ONLINE" : "OFFLINE"}
              </span>
            </div>

            <div className="meta">
              <div>
                <strong>Email:</strong> {doctor.email}
              </div>
              {doctor.phone ? (
                <div>
                  <strong>Phone:</strong> {doctor.phone}
                </div>
              ) : null}
              {doctor.specialties ? (
                <div>
                  <strong>Specialty:</strong> {doctor.specialties}
                </div>
              ) : null}
              {doctor.country ? (
                <div>
                  <strong>Country:</strong> {doctor.country}
                </div>
              ) : null}
              {doctor.state ? (
                <div>
                  <strong>State:</strong> {doctor.state}
                </div>
              ) : null}
              {doctor.medical_license_number ? (
                <div>
                  <strong>License #:</strong> {doctor.medical_license_number}
                </div>
              ) : null}
              {doctor.status ? (
                <div>
                  <strong>Status:</strong> {doctor.status}
                </div>
              ) : null}
            </div>

            <div className="presence-row">
              <label className="presence-toggle">
                <input type="checkbox" checked={!!doctor.is_online} onChange={(e) => setPresence(e.target.checked)} />
                <span>I am available (Online)</span>
              </label>
              <div className="presence-hint">When Online, you’ll connect to realtime and can receive chat/video requests.</div>

              <div style={{ marginTop: 6, fontWeight: 800, opacity: 0.85 }}>
                Realtime:{" "}
                {wsStatus === "connected" ? "Connected ✅" : wsStatus === "connecting" ? "Connecting…" : "Disconnected"}
              </div>
            </div>

            {websiteHref ? (
              <a className="link" href={websiteHref} target="_blank" rel="noreferrer">
                Visit my website
              </a>
            ) : null}

            <div className="doc-links">
              {doctor.license_url ? (
                <a className="link" href={resolveUrl(doctor.license_url)} target="_blank" rel="noreferrer">
                  View uploaded license
                </a>
              ) : null}
              {doctor.cv_url ? (
                <a className="link" href={resolveUrl(doctor.cv_url)} target="_blank" rel="noreferrer">
                  View uploaded CV/Resume
                </a>
              ) : null}
            </div>
          </div>
        </div>

        {doctor.bio ? (
          <div className="doctor-portal-bio" style={{ marginTop: 10 }}>
            <div className="section-title">Bio</div>
            <div className="bio-text">{doctor.bio}</div>
          </div>
        ) : null}

        <div className="doctor-portal-divider" style={{ margin: "14px 0" }} />

        <div className="doctor-portal-edit">
          <div className="edit-top">
            <div>
              <div className="section-title">Request Profile Changes</div>
              <div className="edit-subtext">Changes are submitted to admin and only go live after approval.</div>
            </div>

            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setEditOpen((v) => !v);
                setErr("");
                setMsg("");
                syncFormFromDoctor(doctor);
                setNewPhoto(null);
              }}
            >
              {editOpen ? "Close" : "Edit"}
            </button>
          </div>

          {editOpen ? (
            <form className="edit-form" onSubmit={submitUpdateRequest}>
              <div className="grid-2">
                <div>
                  <label>Full Name</label>
                  <input name="name" value={form.name} onChange={handleEditChange} />
                </div>
                <div>
                  <label>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleEditChange} />
                </div>
              </div>

              <div className="grid-2">
                <div>
                  <label>Specialty</label>
                  <input name="specialties" value={form.specialties} onChange={handleEditChange} />
                </div>
                <div>
                  <label>Country</label>
                  <input name="country" value={form.country} onChange={handleEditChange} />
                </div>
              </div>

              <div className="grid-2">
                <div>
                  <label>State</label>
                  <input name="state" value={form.state} onChange={handleEditChange} />
                </div>
                <div>
                  <label>Website</label>
                  <input name="website" value={form.website} onChange={handleEditChange} />
                </div>
              </div>

              <div className="grid-2">
                <div>
                  <label>Medical License #</label>
                  <input name="medical_license_number" value={form.medical_license_number} onChange={handleEditChange} />
                </div>
                <div>
                  <label>Medical Field</label>
                  <input name="medical_field" value={form.medical_field} onChange={handleEditChange} />
                </div>
              </div>

              <div className="grid-2">
                <div>
                  <label>Status</label>
                  <input name="status" value={form.status} onChange={handleEditChange} />
                </div>
                <div>
                  <label>New Photo (optional)</label>
                  <input type="file" accept="image/*" onChange={(e) => setNewPhoto(e.target.files?.[0] || null)} />
                </div>
              </div>

              <div>
                <label>Bio</label>
                <textarea name="bio" value={form.bio} onChange={handleEditChange} />
              </div>

              <button className="btn-primary" type="submit" disabled={pending}>
                {pending ? "Submitting..." : "Submit for Admin Approval"}
              </button>
            </form>
          ) : null}
        </div>

        <div className="doctor-portal-divider" style={{ margin: "14px 0" }} />

        {erhAssistanceRequests.length > 0 ? (
          <>
            <div style={{ marginTop: 6 }}>
              <div className="section-title">AURA ERH Consulting Requests</div>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                {erhAssistanceRequests.map((request) => (
                  <div key={request.incident_id} style={{ border: "2px solid #7c3aed", background: "#f5f3ff", borderRadius: 14, padding: 12, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 1000, color: "#5b21b6" }}>Private Consulting Advisor Requested</div>
                      <div style={{ marginTop: 4, fontWeight: 850 }}>Incident: {request.incident_id}</div>
                      <div style={{ marginTop: 3, fontSize: 12, opacity: .78 }}>Incident Lead: {request.incident_lead_name || `Advisor ${request.incident_lead_id || ""}`}</div>
                    </div>
                    <button type="button" className="btn-secondary" onClick={() => void acceptErhAssistance(request)}>Assist & Open ERH</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="doctor-portal-divider" style={{ margin: "14px 0" }} />
          </>
        ) : null}

        <div style={{ marginTop: 6 }}>
          <div className="section-title">Student Chat & Video Requests</div>
          <div style={{ opacity: 0.8, fontWeight: 700, marginTop: 6 }}>
            Turn <b>Online</b> to receive requests. When a student requests chat/video, it will appear here.
          </div>

          {incomingInvites.length === 0 ? (
            <div style={{ marginTop: 10, fontWeight: 800, opacity: 0.75 }}>No pending requests.</div>
          ) : (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              {incomingInvites.map((inv) => (
                <div
                  key={`${inv.room_id}_${inv.invite_id}`}
                  style={{
                    borderRadius: 14,
                    border: inv.aura_sos ? "2px solid #dc2626" : "1px solid rgba(0,0,0,0.10)",
                    background: inv.aura_sos ? "rgba(254,226,226,0.92)" : "rgba(255,255,255,0.65)",
                    padding: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: 900 }}>
                    {inv.aura_sos ? "🚨 AURA ERH EMERGENCY ALERT" : inv.mode === "video" ? "🎥 Video request" : "💬 Chat request"}
                    {inv.aura_sos && inv.incident_id ? (
                      <div style={{ marginTop: 4, color: "#991b1b", fontWeight: 950 }}>Incident: {inv.incident_id}</div>
                    ) : null}
                    <div style={{ fontWeight: 800, opacity: 0.75, marginTop: 4, fontSize: 12 }}>
                      Room: {inv.room_id}
                      {inv.from_student_pk ? ` • StudentPK: ${inv.from_student_pk}` : ""}
                      {inv.display_name ? ` • Name: ${inv.display_name}` : ""}
                      {inv.org_code ? ` • Org: ${inv.org_code}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="btn-secondary" type="button" onClick={() => setActiveInvite(inv)}>
                      View
                    </button>
                    <button className="btn-danger" type="button" onClick={() => declineInvite(inv)}>
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="doctor-portal-divider" style={{ margin: "14px 0" }} />

        <div style={{ marginTop: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div className="section-title">Student Session</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 900, fontSize: 13 }}>
              🌐 Chat language
              <select
                value={chatLanguage}
                onChange={(e) => setChatLanguage(e.target.value)}
                dir={isRtlLanguage(chatLanguage) ? "rtl" : "ltr"}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid rgba(0,0,0,0.18)",
                  fontWeight: 850,
                  background: "rgba(255,255,255,0.9)",
                }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option
                    key={lang.code}
                    value={lang.code}
                    dir={lang.rtl ? "rtl" : "ltr"}
                  >
                    {lang.nativeLabel === lang.label
                      ? lang.label
                      : `${lang.label} — ${lang.nativeLabel}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={{ opacity: 0.8, fontWeight: 700, marginTop: 6 }}>
            Accept an incoming request to auto-join a room — or manually join using <b>Student PK</b> below.
            {chatLanguage !== "en" ? (
              <span> Incoming student messages are translated for the advisor view. Advisor replies are sent in the selected language.</span>
            ) : null}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <input
              value={chatStudentPk}
              onChange={(e) => setChatStudentPk(e.target.value)}
              placeholder="Student PK (example: 12)"
              style={{
                flex: "1 1 240px",
                minWidth: 220,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.18)",
                fontWeight: 800,
                boxSizing: "border-box",
              }}
            />
            <button type="button" className="btn-secondary" onClick={startManualChat}>
              Join Room
            </button>
            <button type="button" className="btn-secondary" onClick={disconnectWs}>
              Disconnect
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 8,
              fontWeight: 800,
              opacity: 0.85,
            }}
          >
            <div>
              Status:{" "}
              {wsStatus === "connected" ? "Connected ✅" : wsStatus === "connecting" ? "Connecting…" : "Disconnected"}
            </div>
            <div style={{ fontSize: 12, opacity: 0.65 }}>{chatRoomId ? `Room: ${chatRoomId}` : ""}</div>
          </div>

          {wsError ? (
            <div className="notice error" style={{ marginTop: 10 }}>
              {wsError}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 10,
              borderRadius: 14,
              border: "1px solid rgba(0,0,0,0.08)",
              background: "rgba(255,255,255,0.55)",
              minHeight: 150,
              maxHeight: 220,
              overflowY: "auto",
              padding: 10,
            }}
          >
            {chatMessages.length === 0 ? (
              <div style={{ fontWeight: 700, opacity: 0.8, lineHeight: 1.5 }}>
                No messages yet. Accept a request or join a room above, then type below.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {chatMessages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: m.from === "doctor" ? "flex-end" : "flex-start",
                      maxWidth: "78%",
                      padding: "10px 12px",
                      borderRadius: 16,
                      border: "1px solid rgba(0,0,0,0.08)",
                      background:
                        m.from === "doctor"
                          ? "rgba(36,198,220,0.12)"
                          : m.from === "student"
                            ? "rgba(255,255,255,0.85)"
                            : "rgba(0,0,0,0.06)",
                      fontWeight: 750,
                      lineHeight: 1.35,
                      whiteSpace: "pre-wrap",
                      opacity: m.pending ? 0.65 : 1,
                    }}
                  >
                    {m.text}
                    {m.translated && m.originalText ? (
                      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.62, fontWeight: 700 }}>
                        Original: {m.originalText}
                      </div>
                    ) : null}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <input
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              placeholder="Type message…"
              dir={isRtlLanguage(chatLanguage) ? "rtl" : "ltr"}
              style={{
                flex: 1,
                padding: "12px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.18)",
                fontWeight: 700,
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  sendChat();
                }
              }}
            />
            <button type="button" className="btn-primary" onClick={sendChat} disabled={isTranslatingChat}>
              {isTranslatingChat ? "Translating..." : "Send"}
            </button>
          </div>
        </div>

        <div className="doctor-portal-divider" style={{ margin: "14px 0" }} />

        <div style={{ marginTop: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div>
              <div className="casey-academy-title-row">
                <div className="section-title casey-academy-heading">Casey Academy</div>
                <span className="casey-academy-badge">Version 1.1 Foundation</span>
              </div>
              <div style={{ opacity: 0.78, fontWeight: 750, marginTop: 4 }}>
                Practical wellness guidance, advisor resources, and approved educational content.
              </div>
              {tipsError ? (
                <div className="notice error" style={{ marginTop: 10 }}>
                  {tipsError}
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button type="button" onClick={openTipModal} style={submitTipBtnStyle}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>💡</span>
                <span>Contribute to Casey Academy</span>
              </button>

              <button type="button" className="btn-secondary" onClick={() => setTipsOpen((v) => !v)}>
                {tipsOpen ? "Hide" : "Show"}
              </button>

              <button type="button" className="btn-secondary" onClick={refreshTips} disabled={tipsLoading}>
                {tipsLoading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {tipsOpen ? (
            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12,
              }}
            >
              {tipSections.map(tipSectionCard)}
            </div>
          ) : null}

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7, fontWeight: 750, lineHeight: 1.45 }}>
            Note: Casey Academy content provides general wellness guidance and is not a substitute for diagnosis or emergency care. If a student is in immediate danger,
            follow your local emergency protocol.
          </div>
        </div>

        <div className="doctor-portal-footer" style={{ marginTop: 12 }}>
          © {year} Doctor Is In App. All rights reserved.
        </div>
      </div>

      {tipModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeTipModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10050,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(720px, 96vw)",
              background: "#fff",
              borderRadius: 16,
              border: "1px solid rgba(0,0,0,0.12)",
              boxShadow: "0 20px 70px rgba(0,0,0,0.30)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 14,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 1000 }}>Contribute to Casey Academy</div>
                <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 800, marginTop: 3 }}>
                  Submitting as: <b>{doctor?.name || "Doctor"}</b>
                </div>
              </div>

              <button
                type="button"
                onClick={closeTipModal}
                disabled={tipSubmitting}
                style={{
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "#fff",
                  borderRadius: 10,
                  padding: "7px 12px",
                  cursor: tipSubmitting ? "not-allowed" : "pointer",
                  fontWeight: 900,
                  opacity: tipSubmitting ? 0.7 : 1,
                }}
              >
                Close
              </button>
            </div>

            <form onSubmit={submitTip} style={{ padding: 14, boxSizing: "border-box" }}>
              <div style={{ display: "grid", gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Category</div>
                  <select
                    value={tipCategory}
                    onChange={(e) => setTipCategory(e.target.value as any)}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontWeight: 800,
                      background: "#fff",
                    }}
                  >
                    <option value="physical">Physical Wellness</option>
                    <option value="mental">Mental Health Care</option>
                    <option value="everyday">Everyday Healthy Habits</option>
                  </select>
                </div>

                <div>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Title (optional)</div>
                  <input
                    value={tipTitle}
                    onChange={(e) => setTipTitle(e.target.value)}
                    placeholder="Short title (optional)…"
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontWeight: 800,
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Academy Contribution</div>
                  <textarea
                    value={tipText}
                    onChange={(e) => setTipText(e.target.value)}
                    placeholder="Write your Casey Academy contribution here..."
                    rows={5}
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.18)",
                      fontWeight: 700,
                      resize: "vertical",
                      outline: "none",
                    }}
                  />
                </div>

                <div style={{ fontSize: 12, opacity: 0.75, fontWeight: 750 }}>
                  Casey Academy contributions are reviewed by Admin before they become available.
                </div>

                {tipNotice ? (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 12,
                      background: tipNotice.toLowerCase().includes("pending") ? "rgba(34,197,94,0.10)" : "rgba(255,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      fontWeight: 900,
                    }}
                  >
                    {tipNotice}
                  </div>
                ) : null}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={closeTipModal}
                    disabled={tipSubmitting}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: "rgba(255,255,255,0.95)",
                      cursor: tipSubmitting ? "not-allowed" : "pointer",
                      fontWeight: 900,
                      opacity: tipSubmitting ? 0.7 : 1,
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={tipSubmitting}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 12,
                      border: "none",
                      background: "rgba(37,99,235,0.98)",
                      color: "#fff",
                      cursor: tipSubmitting ? "not-allowed" : "pointer",
                      fontWeight: 950,
                      opacity: tipSubmitting ? 0.75 : 1,
                    }}
                  >
                    {tipSubmitting ? "Submitting..." : "Submit Contribution"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {activeVideoRoomId ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
            zIndex: 9999,
          }}
          onClick={() => setActiveVideoRoomId("")}
        >
          <div
            style={{
              width: "min(980px, 100%)",
              borderRadius: 18,
              background: "rgba(255,255,255,0.96)",
              border: "1px solid rgba(255,255,255,0.65)",
              boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
              padding: 14,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {wsRef.current && wsRef.current.readyState === WebSocket.OPEN ? (
              <VideoRoomAny
                ws={wsRef.current}
                roomId={activeVideoRoomId}
                room_id={activeVideoRoomId}
                role="doctor"
                onHangup={() => setActiveVideoRoomId("")}
                onEnd={() => setActiveVideoRoomId("")}
              />
            ) : (
              <div className="notice error" style={{ marginTop: 10 }}>
                Realtime socket not connected. Toggle Online first.
              </div>
            )}
          </div>
        </div>
      ) : null}

      {activeInvite ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
            zIndex: 10000,
          }}
          onClick={() => setActiveInvite(null)}
        >
          <div
            style={{
              width: "min(560px, 100%)",
              borderRadius: 18,
              background: "rgba(255,255,255,0.96)",
              border: "1px solid rgba(255,255,255,0.65)",
              boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
              padding: 18,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div style={{ fontWeight: 1000, fontSize: 18 }}>
                {activeInvite.aura_sos ? "🚨 AURA ERH Emergency Alert" : activeInvite.mode === "video" ? "🎥 Video Request" : "💬 Chat Request"}
              </div>
              <button type="button" className="btn-secondary" onClick={() => setActiveInvite(null)}>
                Close
              </button>
            </div>

            <div style={{ marginTop: 12, fontWeight: 900, opacity: 0.85 }}>
              Room: <span style={{ fontWeight: 800 }}>{activeInvite.room_id}</span>
            </div>

            {activeInvite.from_student_pk ? (
              <div style={{ marginTop: 8, fontWeight: 800, opacity: 0.8, fontSize: 13 }}>
                StudentPK: <b>{activeInvite.from_student_pk}</b>
                {activeInvite.display_name ? ` • ${activeInvite.display_name}` : ""}
              </div>
            ) : null}

            <div style={{ marginTop: 14, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="btn-danger" onClick={() => declineInvite(activeInvite)}>
                Decline
              </button>
              <button type="button" className="btn-primary" onClick={() => acceptInvite(activeInvite)}>
                {activeInvite.aura_sos ? "Accept & Open ERH" : "Accept & Join"}
              </button>
            </div>

            <div style={{ marginTop: 12, fontSize: 12, opacity: 0.75, fontWeight: 700 }}>
              Accepting will send <b>invite.accept</b> to the student and open video for video requests.
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DoctorPortal;
